import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { publicGalleryUrl } from "../../helpers/publicUploads";
import { useGalleryLinkLocale } from "../../helpers/galleryLinkLocale";
import { hasNoBalance, isEventPublished, isPublicGalleryOn, publishEventWarnings } from "../../helpers/eventPublication";
import { onPhotosChanged } from "../../helpers/photosChanged";
import { ownsEvent } from "../../helpers/eventSponsors";
import { announceEventComplete, setPublicGallery } from "../../redux/actions/events";
import StatsService from "../../services/statsService";
import { toast } from "react-toastify";
import { ERROR_TOAST_CONFIG, formatShortDate } from "../../helpers/functions";
import { RevButton, RevChip, RevPageHead, RevSkeleton } from "../ds";
import PublishAppModal, { useAnnounceApp } from "./publishAppModal";

const TOAST = { ...ERROR_TOAST_CONFIG, autoClose: 4000 };

/**
 * Chip de publicación de la cabecera. Enlaza a la pestaña Galería pública (solo el dueño la
 * tiene); publicado y sin saldo pasa a tono aviso y lleva a recargar créditos.
 */
const PublicationChip = ({ event, owner, galleryOn, hiddenForBalance, t }) => {
    if (hiddenForBalance) {
        return (
            <RevChip as={Link} to="/account/credit/home" tone="warn" className="rev-chip--link"
                title={t('event.header.chip.publishedHiddenTitle')}>
                {t('event.header.chip.publishedHidden')}
            </RevChip>
        );
    }
    const label = t(galleryOn ? 'event.header.chip.published' : 'event.header.chip.unpublished');
    const tone = galleryOn ? 'ok' : undefined;
    return owner
        ? <RevChip as={Link} to={`/events/${event.id}/public-gallery`} tone={tone} className="rev-chip--link">{label}</RevChip>
        : <RevChip tone={tone}>{label}</RevChip>;
};

const EventHeader = ({ event, guest, eventId, user, cargando, announceEventComplete, setPublicGallery, savingGallery }) => {
    const { t, i18n } = useTranslation();
    const [photos, setPhotos] = useState(undefined);
    useEffect(() => { setPhotos(undefined); }, [event?.id]);
    const [publishOpen, setPublishOpen] = useState(false);
    // Idioma elegido en la pestaña Galería pública para este evento ('' = el de cada visitante).
    const [linkLocale] = useGalleryLinkLocale(event?.id);
    const { announcing, announce } = useAnnounceApp({
        eventId: event?.id,
        code: event?.code,
        announce: announceEventComplete,
        formatDate: value => (value ? new Date(value).toLocaleString(i18n?.language) : ""),
    });

    // El recuento se vuelve a pedir al cambiar de evento, al cambiar de pestaña y cuando
    // Subir fotos o Fotos avisan de una subida o un borrado: si no, la cabecera se quedaba
    // con el 0 de un evento recién creado y «Terminar subida y activar selfies» seguía deshabilitado.
    const { pathname } = useLocation();
    useEffect(() => {
        let alive = true;
        if (!event?.id) { setPhotos(undefined); return undefined; }

        const load = () => StatsService.getNumberOfPhotos(event.id)
            .then(response => {
                const raw = response?.data?.Data;
                if (alive) setPhotos(raw === undefined || raw === null ? null : Number(raw));
            })
            .catch(() => { if (alive) setPhotos(null); });

        load();
        const stop = onPhotosChanged(event.id, load);
        return () => { alive = false; stop(); };
    }, [event?.id, pathname]);

    if (!event?.id) {
        // Los rótulos son los reales traducidos que pintaría el estado cargado: el molde
        // envuelve donde envuelve el real en cada idioma.
        return cargando
            ? <RevSkeleton.Cabecera acciones={guest
                ? [t('event.sideNavigation.uploadPhotos')]
                : [t('event.header.copyGalleryLink'), t('event.header.activateSelfies')]} />
            : null;
    }

    const locale = t('locale', { returnObjects: true }) || 'es-ES';
    const dateFrom = formatShortDate(event.dateFrom, locale);
    // Dos estados independientes: selfies activos (announce-complete, irreversible) y evento
    // publicado (galería pública + listado de la app, reversible desde su pestaña).
    const selfiesOn = isEventPublished(event);
    const galleryOn = isPublicGalleryOn(event);
    const owner = !guest && ownsEvent(event, user);
    // Publicado pero fuera del listado de la app: el backend oculta los eventos de un dueño sin
    // saldo. Solo lo sabe el dueño (es su saldo el que llega de GET /user).
    const hiddenForBalance = owner && galleryOn && hasNoBalance(user);

    const unitPrice = event?.pricingData?.unitPrice;
    const hasPrice = unitPrice !== undefined && unitPrice !== null && unitPrice !== '';
    let priceLabel = null;
    if (!guest && hasPrice) {
        try {
            const price = new Intl.NumberFormat(locale, { style: 'currency', currency: user?.currency || 'EUR' }).format(unitPrice);
            priceLabel = t('event.header.pricePerPhoto', { price });
        } catch (e) {
            priceLabel = null;
        }
    }

    const photosKnown = photos !== undefined && photos !== null && !Number.isNaN(photos);
    let photosLabel = '—';
    if (photosKnown) {
        photosLabel = photos > 0 ? t('event.header.photos', { count: photos }) : t('event.header.photosNone');
    }

    const meta = (
        <>
            {dateFrom && <span className="rev-num">{dateFrom}</span>}
            {event.code && <span className="rev-pagehead__kv">
                {t('event.header.code')}
                <span className="rev-mono rev-code-chip">{event.code}</span>
            </span>}
            <span className="rev-num">{photosLabel}</span>
            {priceLabel && <span className="rev-num">{priceLabel}</span>}
            {guest && <RevChip tone="info">{t('event.guest.shared')}</RevChip>}
            {!guest && <PublicationChip event={event} owner={owner} galleryOn={galleryOn} hiddenForBalance={hiddenForBalance} t={t} />}
            {/* «Subida abierta» lleva a la pestaña Subir fotos; con los selfies activos no hay a dónde ir. */}
            {!guest && (selfiesOn
                ? <RevChip tone="ok">{t('event.header.chip.selfiesOn')}</RevChip>
                : <RevChip as={Link} to={`/events/${event.id}/photos`} tone="warn" className="rev-chip--link">
                    {t('event.header.chip.uploadOpen')}
                </RevChip>)}
        </>
    );

    // Sin REACT_APP_GALLERY_URL no hay destino al que abrir: con la galería apagada tampoco
    // hay página que ver (la pestaña Galería pública enseña el estado y el interruptor).
    const galleryUrl = publicGalleryUrl(event.code, linkLocale);
    const copyGalleryLink = async () => {
        try {
            await navigator.clipboard.writeText(galleryUrl);
            toast.success(t('publicGallery.linkCopied'), TOAST);
        } catch {
            toast.error(t('publicGallery.copyError'), TOAST);
        }
    };

    const onActivateSelfies = async () => {
        setPublishOpen(false);
        await announce();
    };

    // «Publicar evento» desde la cabecera: el mismo PUT que el interruptor de la pestaña.
    // Con 0 fotos públicas avisa sin bloquear (la galería se verá vacía).
    const onPublishEvent = async () => {
        if (savingGallery || typeof setPublicGallery !== 'function') return;
        try {
            const result = await setPublicGallery(event.id, true);
            toast.success(t(hasNoBalance(user) ? 'publicGallery.status.toast.onHidden' : 'publicGallery.status.toast.on'), TOAST);
            publishEventWarnings({ result, active: true, publicPhotoCount: event.publicPhotoCount })
                .forEach(key => toast.warning(t(key), TOAST));
        } catch (error) {
            const status = error?.response?.status;
            const reason = status === 403 ? 'forbidden' : status === 404 ? 'notFound' : 'generic';
            toast.error(t(`publicGallery.status.error.${reason}`), TOAST);
        }
    };

    const actions = (
        <>
            {!guest && galleryOn && galleryUrl && <>
                <RevButton variant="secondary" data-pendiente="ds-enlace-galeria" onClick={copyGalleryLink}>
                    {t('event.header.copyGalleryLink')}
                    {linkLocale && <span className="rev-mono rev-code-chip rev-pagehead__link-lang" aria-hidden="true">{linkLocale.toUpperCase()}</span>}
                    {linkLocale && <span className="rev-sr-only">{t(`publicGallery.link.languages.${linkLocale}`)}</span>}
                </RevButton>
                <RevButton as="a" variant="secondary" icon data-pendiente="ds-enlace-galeria"
                    href={galleryUrl} target="_blank" rel="noopener noreferrer"
                    aria-label={t('event.header.openGallery')} title={t('event.header.openGallery')}>
                    <span className="material-icons">open_in_new</span>
                </RevButton>
            </>}
            {/* Una sola acción principal, la del siguiente paso: primero el irreversible
                (activar selfies), luego publicar; con las dos hechas no hay ninguna. */}
            {owner && !selfiesOn &&
                <RevButton variant="primary" loading={announcing}
                    disabled={photosKnown && photos === 0}
                    title={photosKnown && photos === 0 ? t('event.header.activateNeedsPhotos') : undefined}
                    onClick={() => setPublishOpen(true)}>
                    {t('event.header.activateSelfies')}
                </RevButton>}
            {owner && selfiesOn && !galleryOn &&
                <RevButton variant="primary" loading={!!savingGallery} onClick={onPublishEvent}
                    title={t('event.header.publishEventTitle')}>
                    {t('event.header.publishEvent')}
                </RevButton>}
            {guest && !selfiesOn && <RevButton as={Link} to={`/events/${eventId}/shared-photo`} variant="primary">
                {t('event.sideNavigation.uploadPhotos')}
            </RevButton>}
        </>
    );

    return (
        <>
            <RevPageHead
                className="rev-pagehead--tabs"
                title={event.name}
                meta={meta}
                actions={actions}
            />
            {publishOpen && (
                <PublishAppModal
                    eventId={event.id}
                    event={event}
                    loading={announcing}
                    onConfirm={onActivateSelfies}
                    onCancel={() => setPublishOpen(false)}
                />
            )}
        </>
    );
};

const mapStateToProps = state => ({
    savingGallery: state.eventsReducer.loadingPublicGallery,
});

const mapDispatchToProps = { announceEventComplete, setPublicGallery };

export default connect(mapStateToProps, mapDispatchToProps)(EventHeader);
